import type { AppError } from './types.js';

export class ApiError extends Error {
  readonly errors: AppError[];
  readonly statusCode: number;
  /** Backend triage id from the error envelope's `requestId`. Absent for
   * responses that didn't pass through the central handler (e.g. an upstream
   * 404 with no envelope). */
  readonly requestId?: string | undefined;

  constructor(errors: AppError[], statusCode: number, requestId?: string) {
    super(errors.map((e) => e.message).join('; ') || `HTTP ${statusCode}`);
    this.name = 'ApiError';
    this.errors = errors;
    this.statusCode = statusCode;
    this.requestId = requestId;
  }
}

export async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // Response wasn't JSON; fall through and let the !ok branch surface the
      // raw status text below.
    }
  }
  if (!response.ok) {
    if (
      data !== null &&
      typeof data === 'object' &&
      'success' in data &&
      (data as { success: unknown }).success === false &&
      'errors' in data &&
      Array.isArray((data as { errors: unknown }).errors)
    ) {
      throw new ApiError(
        (data as { errors: AppError[] }).errors,
        response.status,
        (data as { requestId?: string }).requestId,
      );
    }
    throw new ApiError(
      [
        {
          code: 'INTERNAL_ERROR',
          message: text || `HTTP ${response.status}`,
        },
      ],
      response.status,
    );
  }
  return data as T;
}
